import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

export function parseUnixTimestamp(
  timestamp: number,
  timezone?: string,
): dayjs.Dayjs {
  return dayjs.unix(timestamp).tz(timezone);
}

/**
 * Convert a calendar date picked in a date-range column filter into a unix
 * timestamp for the *start of that civil day* anchored in `timezone`.
 *
 * react-day-picker hands back a Date at the browser's local midnight, so the
 * previous `dayjs(date).unix()` computed day boundaries in the operator's own
 * timezone. That silently disagrees with how rows are displayed — events render
 * in their own `timezone` — and the gap crosses a calendar day once events span
 * far-apart zones (e.g. an Athens operator filtering LA events). Anchoring the
 * boundary to a chosen `timezone` lets "June 26 in America/Los_Angeles" line up
 * with the LA wall-clock dates shown in the table.
 *
 * When `timezone` is omitted the boundary is computed in the browser's local
 * timezone, preserving the previous behaviour.
 */
export function zonedStartOfDayUnix(date: Date, timezone?: string): number {
  const civilDay = dayjs(date).format("YYYY-MM-DD");
  return (timezone ? dayjs.tz(civilDay, timezone) : dayjs(civilDay)).unix();
}

export function replaceTimezone(date: Date, timezone: string) {
  const dayjsDate = dayjs(date);
  // Extract the time components
  const hour = dayjsDate.hour();
  const minute = dayjsDate.minute();
  const second = dayjsDate.second();

  // Create a new date in the target timezone at the start of the day
  let newDate = dayjs.tz(dayjsDate.format("YYYY-MM-DD"), timezone);

  // Set the time to match the original
  newDate = newDate.hour(hour).minute(minute).second(second);

  return newDate.toDate();
}

export function convertToTimezone(date: Date, timezone: string) {
  const dayjsDate = dayjs(date).tz(timezone);

  // Extract the time components
  const hour = dayjsDate.hour();
  const minute = dayjsDate.minute();
  const second = dayjsDate.second();

  // Create a new date in the target timezone at the start of the day
  let newDate = dayjs(dayjsDate.format("YYYY-MM-DD"));

  // Set the time to match the original
  newDate = newDate.hour(hour).minute(minute).second(second);

  return newDate.toDate();
}

/**
 * Prepare a stored absolute datetime (an offset-aware ISO string such as
 * "2026-06-12T23:00:00+02:00") for the create/edit form's DateTimePicker.
 *
 * The picker reads and writes wall-clock components in the *browser's* local
 * timezone, and the form re-stamps them onto the event timezone via
 * `replaceTimezone` on submit. To round-trip correctly the picker must be
 * seeded with a Date whose browser-local wall-clock equals the event-timezone
 * wall-clock — otherwise a curator in a different timezone (e.g. Athens viewing
 * a Berlin event) sees the time shifted by the offset difference.
 *
 * This is the inverse of `replaceTimezone` and mirrors what the draft-load and
 * edit paths already do; the clipboard-prefill path must apply it too.
 */
export function prefillDatetimeForPicker(
  iso: string | undefined,
  timezone: string | undefined,
): string | undefined {
  if (!iso) return undefined;
  const date = new Date(iso);
  if (!timezone) return date.toISOString();
  return convertToTimezone(date, timezone).toISOString();
}
